import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ClerkProvider, SignedIn, SignedOut, SignIn, RedirectToSignIn } from "@clerk/clerk-react";
import LandingPage from "@/pages/LandingPage";
import BlogDetail from "@/pages/BlogDetail";
import PolicyPage from "@/pages/PolicyPage";
import PublicMenu from "@/pages/PublicMenu";
import CustomerOrder from "@/pages/CustomerOrder";
import AdminDashboard from "@/pages/AdminDashboard";
import KitchenDisplay from "@/pages/KitchenDisplay";
import BeverageDisplay from "@/pages/BeverageDisplay";
import ManualBillPage from "@/pages/ManualBillPage";
import StaffLogin from "@/pages/StaffLogin";
import StaffDashboard from "@/pages/StaffDashboard";
import MaintenancePage from "@/pages/MaintenancePage";

const adminClerkKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
const API = process.env.REACT_APP_BACKEND_URL;

const ProtectedRoute = ({ children }) => (
  <>
    <SignedIn>{children}</SignedIn>
    <SignedOut><RedirectToSignIn /></SignedOut>
  </>
);

const PublicRoute = ({ children, maintenance }) => {
  if (maintenance.active) return <MaintenancePage message={maintenance.message} />;
  return children;
};

function App() {
  const [maintenance, setMaintenance] = useState({ active: false, message: '' });

  useEffect(() => {
    fetch(`${API}/api/maintenance-status`)
      .then(r => r.json())
      .then(d => setMaintenance({ active: d.maintenance_mode, message: d.maintenance_message }))
      .catch(() => {});
    const interval = setInterval(() => {
      fetch(`${API}/api/maintenance-status`)
        .then(r => r.json())
        .then(d => setMaintenance({ active: d.maintenance_mode, message: d.maintenance_message }))
        .catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <BrowserRouter>
      <div className="App">
        <Routes>
          {/* Public Routes - Subject to maintenance mode */}
          <Route path="/" element={<PublicRoute maintenance={maintenance}><LandingPage /></PublicRoute>} />
          <Route path="/menu" element={<PublicRoute maintenance={maintenance}><PublicMenu /></PublicRoute>} />
          <Route path="/order" element={<PublicRoute maintenance={maintenance}><CustomerOrder /></PublicRoute>} />
          <Route path="/blog" element={<PublicRoute maintenance={maintenance}><LandingPage /></PublicRoute>} />
          <Route path="/blog/:id" element={<PublicRoute maintenance={maintenance}><BlogDetail /></PublicRoute>} />
          <Route path="/privacy-policy" element={<PolicyPage />} />
          <Route path="/terms" element={<PolicyPage />} />
          <Route path="/cookie-policy" element={<PolicyPage />} />
          <Route path="/disclaimer" element={<PolicyPage />} />

          {/* Staff/Admin Routes - Always accessible */}
          <Route path="/kitchen" element={<KitchenDisplay />} />
          <Route path="/beverages" element={<BeverageDisplay />} />
          <Route path="/staff" element={<StaffLogin />} />
          <Route path="/staff/dashboard" element={<StaffDashboard />} />
          
          {/* Disabled (restaurant-only mode): /attractions, /rentals, /partner*, /services* routes are removed. */}
          
          {/* Admin Routes - Protected with Clerk */}
          <Route
            path="/sign-in/*"
            element={
              <ClerkProvider publishableKey={adminClerkKey}>
                <div className="flex items-center justify-center min-h-screen bg-slate-50">
                  <SignIn routing="path" path="/sign-in" afterSignInUrl="/admin" />
                </div>
              </ClerkProvider>
            }
          />
          <Route
            path="/admin"
            element={
              <ClerkProvider publishableKey={adminClerkKey}>
                <ProtectedRoute><AdminDashboard /></ProtectedRoute>
              </ClerkProvider>
            }
          />
          <Route
            path="/pos"
            element={
              <ClerkProvider publishableKey={adminClerkKey}>
                <ProtectedRoute><ManualBillPage /></ProtectedRoute>
              </ClerkProvider>
            }
          />

          {/* Catch-all: any removed/unknown route → redirect to landing page.
              This covers disabled sections (/attractions, /rentals, /partner*,
              /services*) so visitors or old bookmarks don't land on an empty page. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
